/**
 * <StaggerItem> — a child of <Stagger>. Inherits the parent's hidden/show
 * orchestration via `itemVariants`; renders statically under reduced motion.
 *
 * For lists, render `<StaggerItem as="li">` IN PLACE of the existing `<li>`
 * (keep the `data-testid` on this node) rather than adding an outer wrapper —
 * that keeps the e2e selector on the same element.
 */
import { motion } from "framer-motion";
import { forwardRef, type Ref } from "react";

import { MOTION_TAGS, type MotionWrapperProps } from "@/components/motion/shared";
import { itemVariants } from "@/lib/motion";
import { useGardenMotion } from "@/lib/use-garden-motion";

export const StaggerItem = forwardRef<HTMLElement, MotionWrapperProps>(
  function StaggerItem({ as = "div", className, children, "data-testid": testId }, ref) {
    const { reduced } = useGardenMotion();
    const Comp = MOTION_TAGS[as] as typeof motion.div;
    return (
      <Comp
        ref={ref as Ref<HTMLDivElement>}
        className={className}
        data-testid={testId}
        variants={reduced ? undefined : itemVariants}
      >
        {children}
      </Comp>
    );
  },
);
